import React, { useState } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { getDatabase, ref, push, set, get, update, serverTimestamp } from "firebase/database";
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Alert } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { ThreeDots } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';
import { getAvailableTimeSlotsForDate, getBookingSourceData, getLockedTimeConflict, getLockedTimeRangesForDate } from './utils';
import { notifyNewBooking } from './utils/adminNotifications';
import { notifyOwnerNewBooking } from './utils/userNotifications';

/**
 * Shared modal component for instant booking of packages
 * Used in both SingleListingLayout and ListingPackages
 */
const PackageInstantBookModal = ({
    open,
    onClose,
    selectedPackage,
    listing,
    currentUser,
    disabledDates = [],
    lockedDates = {},
}) => {
    console.log('selectedPackage...', selectedPackage)
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();

    const [packageBookingDate, setPackageBookingDate] = useState(null);
    const [packageBookingTime, setPackageBookingTime] = useState(null);
    const [packageBookingError, setPackageBookingError] = useState("");
    const [packageBookingLoading, setPackageBookingLoading] = useState(false);
    const [passengerCount, setPassengerCount] = useState(1);

    // Calculate pricing based on passenger count
    // maxGuests = passengers included in the base price
    // afterGuests = additional passengers allowed beyond the base
    // extraPersonPrice = % added per additional passenger
    const basePrice = parseInt(selectedPackage?.basePrice) || 0;
    const includedPassengers = parseInt(selectedPackage?.maxGuests) || 1;
    const additionalAllowed = parseInt(selectedPackage?.priceChangeOptions?.afterGuests) || 0;
    const extraType = selectedPackage?.priceChangeOptions?.extraPersonPriceType || "percentage";
    const extraVal = parseInt(selectedPackage?.priceChangeOptions?.extraPersonPrice) || 0;
    const extraChargePerPerson = extraType === "flat" ? extraVal : (basePrice * extraVal) / 100;
    const totalMaxPassengers = includedPassengers + additionalAllowed;
    const extraPassengers = Math.max(0, passengerCount - includedPassengers);
    const totalExtraCharge = extraPassengers * extraChargePerPerson;
    const totalPrice = basePrice + totalExtraCharge;

    const handleClose = () => {
        setPackageBookingDate(null);
        setPackageBookingTime(null);
        setPackageBookingError("");
        setPassengerCount(1);
        onClose();
    };

    const getPackageDuration = () => {
        const durationText = selectedPackage?.duration || "";
        let sameDayHours = 0;
        let overnight = 0;

        const hoursMatch = durationText.match(/(\d+)\s*hour/i);
        const nightsMatch = durationText.match(/(\d+)\s*night/i);
        const daysMatch = durationText.match(/(\d+)\s*day/i);
        const plainNumberMatch = durationText.match(/^(\d+)$/);

        if (hoursMatch) {
            sameDayHours = parseInt(hoursMatch[1]);
        }
        if (nightsMatch) {
            overnight = parseInt(nightsMatch[1]);
        }
        if (daysMatch && !hoursMatch) {
            overnight = parseInt(daysMatch[1]);
        }
        if (!hoursMatch && !nightsMatch && !daysMatch && plainNumberMatch) {
            sameDayHours = parseInt(plainNumberMatch[1]);
        }

        return { sameDayHours, overnight };
    };

    const lockedTimeRangesForSelectedDate = getLockedTimeRangesForDate(
        lockedDates,
        packageBookingDate ? dayjs(packageBookingDate).format("dddd, MMMM D, YYYY") : null
    );
    const packageDuration = getPackageDuration();
    const availableTimeSlotsForSelectedDate = getAvailableTimeSlotsForDate(
        lockedDates,
        packageBookingDate ? dayjs(packageBookingDate).format("dddd, MMMM D, YYYY") : null,
        packageDuration.sameDayHours,
        0,
        { intervalMinutes: 60 }
    );
    const normalizeTimeLabel = (time) => {
        const match = String(time).trim().match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
        if (!match) return String(time).trim();
        return `${match[1].padStart(2, "0")}:${match[2]} ${match[3].toUpperCase()}`;
    };
    const packageStartOptions = selectedPackage?.startTimeOptions?.length
        ? selectedPackage.startTimeOptions.map((time) => normalizeTimeLabel(time))
        : null;
    const availablePackageStartTimes = packageStartOptions
        ? availableTimeSlotsForSelectedDate.filter((slot) => packageStartOptions.includes(slot.value))
        : availableTimeSlotsForSelectedDate;

    const getPackageLockedTimeConflict = () => {
        if (!packageBookingDate || !packageBookingTime) {
            return null;
        }

        const { sameDayHours, overnight } = getPackageDuration();
        return getLockedTimeConflict(
            lockedDates,
            dayjs(packageBookingDate).format("dddd, MMMM D, YYYY"),
            packageBookingTime,
            sameDayHours,
            0,
            overnight
        );
    };

    // Handle package instant booking - create booking and go to payment
    const handlePackageInstantBook = async () => {
        if (!packageBookingDate || !packageBookingTime) {
            setPackageBookingError("Please select both date and time");
            return;
        }

        if (!currentUser) {
            setPackageBookingError("You must be logged in to book.");
            return;
        }

        const { sameDayHours, overnight } = getPackageDuration();
        const formattedDate = dayjs(packageBookingDate).format("dddd, MMMM D, YYYY");
        const formattedTime = packageBookingTime;
        const lockedTimeConflict = getPackageLockedTimeConflict();

        if (lockedTimeConflict) {
            setPackageBookingError(`This listing is locked on ${dayjs(lockedTimeConflict.date).format("MMMM D, YYYY")} from ${lockedTimeConflict.start} to ${lockedTimeConflict.end}. Please choose another time.`);
            return;
        }

        // Validate date is within package availability range if specified
        if (selectedPackage?.availableDateRange?.startDate && selectedPackage?.availableDateRange?.endDate) {
            const selectedDate = dayjs(packageBookingDate);
            const startDate = dayjs(selectedPackage.availableDateRange.startDate);
            const endDate = dayjs(selectedPackage.availableDateRange.endDate);

            if (selectedDate.isBefore(startDate) || selectedDate.isAfter(endDate)) {
                setPackageBookingError(`Please select a date between ${selectedPackage.availableDateRange.startDate} and ${selectedPackage.availableDateRange.endDate}`);
                return;
            }
        }

        // Validate day is offered if daysOffered is specified
        if (selectedPackage?.daysOffered) {
            const dayName = dayjs(packageBookingDate).format('ddd');
            if (!selectedPackage.daysOffered[dayName]) {
                const offeredDays = Object.entries(selectedPackage.daysOffered)
                    .filter(([_, isOffered]) => isOffered)
                    .map(([day]) => day)
                    .join(', ');
                setPackageBookingError(`This package is only available on: ${offeredDays}`);
                return;
            }
        }

        setPackageBookingLoading(true);
        setPackageBookingError("");

        try {
            const database = getDatabase();
            const bookingRef = ref(database, `bookings`);
            const paymentRef = ref(database, `payments`);
            const inquiryRef = ref(database, `inquiries`);

            // Create new booking
            const newBookingRef = push(bookingRef);
            const bookingId = newBookingRef.key;

            // Create new payment
            const newPaymentRef = push(paymentRef);
            const newPaymentId = newPaymentRef.key;

            // Create new inquiry with offerData for package bookings
            const newInquiryRef = push(inquiryRef);
            const inquiryId = newInquiryRef.key;

            // Build duration text from parsed values
            const parsedDurationText = sameDayHours > 0
                ? `${sameDayHours} Hour${sameDayHours > 1 ? 's' : ''}`
                : overnight > 0
                    ? `${overnight} Night${overnight > 1 ? 's' : ''}`
                    : selectedPackage.duration || '';

            // Create offerData structure similar to regular bookings
            const offerData = {
                offeredDate: formattedDate,
                offeredTime: formattedTime,
                duration: parsedDurationText,
                arivingDateTime: `${formattedDate} ${formattedTime}`,
                offerPrice: totalPrice,
                overnight: overnight,
                offeredHours: sameDayHours,
                pickupAddress: listing?.storageAddress || "",
                pickupAddressString: listing?.storageAddressText || "",
                dropOffAddress: listing?.storageAddress || "",
                dropOffAddressString: listing?.storageAddressText || "",
                captainCheckbox: false,
                trailerCheckbox: false,
                deliveryPickup: false,
                discount: 0
            };

            // Save inquiry data
            await set(newInquiryRef, {
                listingId: listing.id,
                renter: currentUser.uid,
                owner: listing.ownerId,
                inquiryData: {
                    prefferedDate: formattedDate,
                    prefferedTime: formattedTime,
                    sameDayHours: sameDayHours,
                    overnight: overnight,
                    estimatedPrice: totalPrice,
                    numberOfBoats: 1,
                    adults: passengerCount,
                    children: 0,
                    infants: 0,
                    seniors: 0,
                    total: passengerCount,
                    passengers: {
                        adults: passengerCount,
                        children: 0,
                        infants: 0,
                        seniors: 0,
                        total: passengerCount,
                    },
                    comments: ""
                },
                offerData: offerData,
                status: "Accepted", // Since this is instant booking
                timestamp: serverTimestamp()
            });

            // Create a chat message for the instant booking notification
            const chatId1 = `${currentUser.uid}_${listing.ownerId}`;
            const chatId2 = `${listing.ownerId}_${currentUser.uid}`;
            const chatRef1 = ref(database, `chats/${chatId1}`);
            const chatRef2 = ref(database, `chats/${chatId2}`);
            const chatSnapshot1 = await get(chatRef1);
            const chatSnapshot2 = await get(chatRef2);

            const instantBookMessage = `I have just instant booked your package: ${selectedPackage.name} for ${formattedDate} at ${formattedTime}.`;
            const messageData = {
                senderId: currentUser.uid,
                receiverId: listing.ownerId,
                message: instantBookMessage,
                messageMobile: instantBookMessage,
                timestamp: serverTimestamp(),
                inquiryId: inquiryId,
                listingId: listing.id,
            };

            if (chatSnapshot1.exists()) {
                const chatData = chatSnapshot1.val();
                const existingMessages = Array.isArray(chatData.messages) ? chatData.messages : [];
                const updates = {
                    lastSenderId: currentUser.uid,
                    messages: [...existingMessages, messageData],
                };
                if (chatData.senderId === currentUser.uid) {
                    updates.senderReadStatus = 1;
                    updates.receiverReadStatus = 0;
                } else {
                    updates.senderReadStatus = 0;
                    updates.receiverReadStatus = 1;
                }
                await update(chatRef1, updates);
            } else if (chatSnapshot2.exists()) {
                const chatData = chatSnapshot2.val();
                const existingMessages = Array.isArray(chatData.messages) ? chatData.messages : [];
                const updates = {
                    lastSenderId: currentUser.uid,
                    messages: [...existingMessages, messageData],
                };
                if (chatData.senderId === currentUser.uid) {
                    updates.senderReadStatus = 1;
                    updates.receiverReadStatus = 0;
                } else {
                    updates.senderReadStatus = 0;
                    updates.receiverReadStatus = 1;
                }
                await update(chatRef2, updates);
            } else {
                await set(chatRef1, {
                    senderId: currentUser.uid,
                    receiverId: listing.ownerId,
                    lastSenderId: currentUser.uid,
                    senderReadStatus: 1,
                    receiverReadStatus: 0,
                    messages: [messageData],
                });
            }

            // Create booking data with inquiryId
            const bookingData = {
                renter: currentUser.uid,
                owner: listing.ownerId,
                inquiryId: inquiryId, // Link to the created inquiry
                listingTitle: listing.listingTitle,
                priceOffered: totalPrice,
                listingId: listing.id,
                paymentId: newPaymentId,
                totalWaivers: selectedPackage.maxGuests || 0,
                waiversFilled: {
                    adult: 0,
                    minor: 0,
                },
                status: "pending",
                createdAt: serverTimestamp(),
                timestamp: serverTimestamp(),
                tripDate: formattedDate,
                tripTime: formattedTime,
                duration: {
                    sameDayHours: Number(sameDayHours) || 0,
                    sameDayMinutes: 0,
                    overnight: Number(overnight) || 0,
                    text: parsedDurationText
                },
                passengers: {
                    adults: passengerCount,
                    children: 0,
                    infants: 0,
                    seniors: 0,
                    total: passengerCount,
                },
                additionals: {},
                instantBook: true,
                packageBooking: true,
                packageDetails: {
                    name: selectedPackage.name,
                    duration: selectedPackage.duration,
                    basePrice: selectedPackage.basePrice,
                    maxGuests: selectedPackage.maxGuests,
                    description: selectedPackage.description || "",
                },
                comments: `Package: ${selectedPackage.name}`,
                ...getBookingSourceData(),
            };

            // Save booking to database
            await set(newBookingRef, bookingData);

            notifyNewBooking(listing.listingTitle, bookingId).catch((err) =>
              console.error("Failed to create admin notification:", err)
            );
            notifyOwnerNewBooking(listing.ownerId, listing.listingTitle, bookingId).catch(
              (err) => console.error("Failed to create owner notification:", err)
            );

            // Update user's active bookings count
            const userRef = ref(database, `users/${currentUser.uid}`);
            const userSnapshot = await get(userRef);
            const userData = userSnapshot.val();
            const activeBookings = (userData?.activeBookings || 0) + 1;
            await update(userRef, { activeBookings });

            setPackageBookingLoading(false);
            handleClose();

            // Navigate to payment page
            const queryParams = new URLSearchParams({ bookingId });
            navigate(`/dashboard/booking-payment?${queryParams.toString()}`);
        } catch (err) {
            console.error("Error creating package instant booking:", err);
            setPackageBookingError("Failed to create booking. Please try again.");
            setPackageBookingLoading(false);
        }
    };

    if (!selectedPackage) return null;

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            aria-labelledby="package-instant-book-dialog"
            maxWidth="sm"
            fullWidth
        >
            <div className="d-flex justify-content-end">
                <IoClose
                    className="close-icon"
                    style={{
                        color: "red",
                        marginRight: "1rem",
                        marginTop: "1rem",
                        width: "max-content",
                        fontSize: "1.5rem",
                        textDecoration: "none",
                        cursor: "pointer",
                    }}
                    onClick={handleClose}
                />
            </div>
            <DialogTitle id="package-instant-book-dialog">
                <div className="d-flex align-items-center gap-2">
                    <i className="material-icons" style={{ color: '#28a745' }}>flash_on</i>
                    Instant Book: {selectedPackage?.name}
                </div>
            </DialogTitle>
            <DialogContent>
                {/* Package Summary */}
                <div className="p-3 mb-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Row>
                        {selectedPackage?.duration && (
                            <Col xs={6}>
                                <p className="mb-1"><strong>Duration:</strong> {
                                    /^\d+$/.test(selectedPackage.duration)
                                        ? `${selectedPackage.duration} Hours`
                                        : selectedPackage.duration
                                }</p>
                            </Col>
                        )}
                        {selectedPackage?.basePrice && (
                            <Col xs={6}>
                                <p className="mb-1"><strong>Base Price:</strong> ${selectedPackage.basePrice}</p>
                                {selectedPackage?.priceChangeOptions?.afterGuests && (
                                    <p className="mb-1 text-muted" style={{ fontSize: '0.8rem' }}>Up to {selectedPackage.maxGuests} passenger{selectedPackage.maxGuests > 1 ? 's' : ''}</p>
                                )}
                            </Col>
                        )}
                        {selectedPackage?.maxGuests && (
                            <Col xs={6}>
                                <p className="mb-1"><strong>Max Passengers:</strong> {totalMaxPassengers}</p>
                            </Col>
                        )}
                        {extraChargePerPerson > 0 && (
                            <Col xs={6}>
                                <p className="mb-1"><strong>Extra Passenger:</strong> +${extraChargePerPerson} per person</p>
                            </Col>
                        )}
                        {(selectedPackage?.availableDateRange?.startDate || selectedPackage?.availableDateRange?.endDate) && (
                            <Col xs={6}>
                                <p className="mb-1"><strong>Available:</strong> {
                                    selectedPackage?.availableDateRange?.startDate && selectedPackage?.availableDateRange?.endDate
                                        ? `${selectedPackage.availableDateRange.startDate} - ${selectedPackage.availableDateRange.endDate}`
                                        : selectedPackage?.availableDateRange?.startDate
                                            ? `From ${selectedPackage.availableDateRange.startDate}`
                                            : `Until ${selectedPackage.availableDateRange.endDate}`
                                }</p>
                            </Col>
                        )}
                        {selectedPackage?.seasonalAvailability && (
                            <Col xs={6}>
                                <p className="mb-1"><strong>Availability:</strong> {selectedPackage.seasonalAvailability}</p>
                            </Col>
                        )}
                        {selectedPackage?.startTimeOptions && selectedPackage.startTimeOptions.length > 0 && (
                            <Col xs={6}>
                                <p className="mb-1"><strong>Start Times:</strong> {selectedPackage.startTimeOptions.join(', ')}</p>
                            </Col>
                        )}
                        {selectedPackage?.daysOffered && Object.values(selectedPackage.daysOffered).some(v => v) && (
                            <Col xs={12} className="mt-2">
                                <p className="mb-1"><strong>Days Offered:</strong></p>
                                <div className="d-flex gap-1 flex-wrap mt-1">
                                    {[
                                        { key: 'Fri', label: 'Fri' },
                                        { key: 'Sat', label: 'Sat' },
                                        { key: 'Sun', label: 'Sun' },
                                        { key: 'Mon', label: 'Mon' },
                                        { key: 'Tue', label: 'Tue' },
                                        { key: 'Wed', label: 'Wed' },
                                        { key: 'Thu', label: 'Thu' }
                                    ].map((day) => {
                                        const isOffered = selectedPackage.daysOffered[day.key];
                                        return (
                                            <div
                                                key={day.key}
                                                className="d-flex align-items-center justify-content-center border"
                                                style={{
                                                    width: '32px',
                                                    height: '24px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 'bold',
                                                    backgroundColor: isOffered ? '#000' : 'transparent',
                                                    borderColor: isOffered ? '#000' : 'rgba(0,0,0,0.1)',
                                                    color: isOffered ? '#fff' : 'rgba(0,0,0,0.3)',
                                                    fontFamily: '"Fjalla One", sans-serif'
                                                }}
                                                title={day.key}
                                            >
                                                {day.label}
                                            </div>
                                        );
                                    })}
                                </div>
                            </Col>
                        )}
                        {selectedPackage?.freeCancellation && (
                            <Col xs={6}>
                                <p className="mb-1 text-success"><i className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'text-top' }}>check_circle</i> Free Cancellation</p>
                            </Col>
                        )}
                    </Row>
                    {selectedPackage?.description && (
                        <p className="mb-0 mt-2"><strong>Description:</strong> {selectedPackage.description}</p>
                    )}
                </div>

                {/* Passenger Count */}
                <Row className="mb-3">
                    <Col xs={12}>
                        <h6>Number of Passengers</h6>
                        <div className="d-flex align-items-center gap-3">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ width: '36px', height: '36px', borderRadius: '50%', padding: 0, fontSize: '1.2rem', lineHeight: 1 }}
                                onClick={() => setPassengerCount(Math.max(1, passengerCount - 1))}
                                disabled={passengerCount <= 1}
                            >
                                −
                            </Button>
                            <span style={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '80px', textAlign: 'center' }}>
                                {passengerCount}
                            </span>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ width: '36px', height: '36px', borderRadius: '50%', padding: 0, fontSize: '1.2rem', lineHeight: 1 }}
                                onClick={() => setPassengerCount(Math.min(totalMaxPassengers, passengerCount + 1))}
                                disabled={passengerCount >= totalMaxPassengers}
                            >
                                +
                            </Button>
                        </div>
                        <small className="text-muted d-block mt-1">
                            {passengerCount <= includedPassengers
                                ? `${includedPassengers} passenger${includedPassengers > 1 ? 's' : ''} included in base price`
                                : `${extraPassengers} extra passenger${extraPassengers > 1 ? 's' : ''} × $${extraChargePerPerson.toFixed(0)} = +$${totalExtraCharge.toFixed(0)}`
                            }
                        </small>
                    </Col>
                </Row>

                {/* Date Selection */}
                <Row className="mb-3">
                    <Col xs={12}>
                        <h6>Select Date</h6>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                minDate={dayjs().startOf("day")}
                                value={packageBookingDate}
                                onChange={(date) => setPackageBookingDate(date)}
                                label="Trip Date"
                                required
                                shouldDisableDate={(date) => {
                                    const formattedDate = dayjs(date).format("YYYY-MM-DD");
                                    const normalizedDisabledDates = disabledDates.map((d) =>
                                        dayjs(d).format("YYYY-MM-DD")
                                    );

                                    // Disable dates not in package availability range
                                    if (selectedPackage?.availableDateRange?.startDate && selectedPackage?.availableDateRange?.endDate) {
                                        const startDate = dayjs(selectedPackage.availableDateRange.startDate);
                                        const endDate = dayjs(selectedPackage.availableDateRange.endDate);
                                        if (dayjs(date).isBefore(startDate) || dayjs(date).isAfter(endDate)) {
                                            return true;
                                        }
                                    }

                                    // Disable days not offered
                                    if (selectedPackage?.daysOffered) {
                                        const dayName = dayjs(date).format('ddd');
                                        if (!selectedPackage.daysOffered[dayName]) {
                                            return true;
                                        }
                                    }

                                    return normalizedDisabledDates.includes(formattedDate);
                                }}
                                disablePast
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Col>
                </Row>

                {/* Time Selection */}
	                <Row className="mb-3">
	                    <Col xs={12}>
	                        <h6>Available start times</h6>
	                        {selectedPackage?.startTimeOptions && selectedPackage?.startTimeOptions.length > 0 ? (
	                            <Form.Select
	                                value={packageBookingTime || ""}
	                                onChange={(e) => setPackageBookingTime(e.target.value)}
	                                required
	                            >
	                                <option value="">Select a start time</option>
	                                {availablePackageStartTimes.map((slot, index) => (
	                                    <option key={index} value={slot.value}>{slot.label}</option>
	                                ))}
	                            </Form.Select>
	                        ) : (
	                            <div className="d-flex flex-wrap gap-2">
	                                {availablePackageStartTimes.map((slot) => (
	                                    <Button
	                                        key={slot.value}
	                                        variant={packageBookingTime === slot.value ? "dark" : "outline-dark"}
	                                        size="sm"
	                                        onClick={() => setPackageBookingTime(slot.value)}
	                                        style={{ minWidth: "96px" }}
	                                    >
	                                        {slot.label}
	                                    </Button>
	                                ))}
	                            </div>
	                        )}
	                        {availablePackageStartTimes.length === 0 && (
	                            <Alert severity="warning" className="mt-3">
	                                No available start times for this date. Please choose another date.
	                            </Alert>
	                        )}
		                        {lockedTimeRangesForSelectedDate.length > 0 && (
	                            <Alert severity={getPackageLockedTimeConflict() ? "error" : "info"} className="mt-3">
	                                {getPackageLockedTimeConflict()
	                                    ? `This listing is locked on ${dayjs(getPackageLockedTimeConflict().date).format("MMMM D, YYYY")} from ${getPackageLockedTimeConflict().start} to ${getPackageLockedTimeConflict().end}. Please choose another time.`
	                                    : (
	                                        <>
	                                            <strong>Unavailable on this date:</strong>{" "}
	                                            {lockedTimeRangesForSelectedDate
	                                                .map((range) => `${range.start} - ${range.end}`)
	                                                .join(", ")}
	                                        </>
	                                    )}
	                            </Alert>
	                        )}
	                    </Col>
	                </Row>


                {/* Error Message */}
                {packageBookingError && (
                    <Alert severity="error" className="mb-3">{packageBookingError}</Alert>
                )}

                {/* Price Summary */}
                <div className="p-3 text-center" style={{ backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
                    <h5 className="mb-0">Total: ${totalPrice.toFixed(0)}</h5>
                    {extraPassengers > 0 && (
                        <small className="d-block" style={{ color: '#555' }}>
                            ${basePrice} (base for {includedPassengers} passenger{includedPassengers > 1 ? 's' : ''}) + ${totalExtraCharge.toFixed(0)} ({extraPassengers} extra × ${extraChargePerPerson.toFixed(0)} each)
                        </small>
                    )}
                    <small className="text-muted">You will be redirected to payment after booking</small>
                </div>
            </DialogContent>
            <DialogActions className="p-3">
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                {packageBookingLoading ? (
                    <Button variant="success" disabled>
                        <ThreeDots height="22" width="22" color="#fff" radius="20" />
                    </Button>
                ) : (
                    <Button
                        variant="success"
                        onClick={handlePackageInstantBook}
                        className="d-flex align-items-center gap-1"
                    >
                        <i className="material-icons" style={{ fontSize: '1.2rem' }}>flash_on</i>
                        Book Now & Pay
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PackageInstantBookModal;
